/**
 * Data layer for `bonus-structure-display-for-partners`.
 * Mirrors BE shape at apps/api/src/routes/bonus-structure-display-for-partners.ts.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export interface BonusTierRow {
	id: number;
	tenant_id: string;
	bonus_id: string;
	campaign_id: string;
	tier_name: string;
	threshold_conversions: number;
	bonus_amount_cents: number;
	starts_at: number;
	ends_at: number;
	display_order: number;
	created_at: number;
}

export interface ListBonusStructureResponse {
	items: BonusTierRow[];
}

export interface PartnerBonusResponse {
	items: BonusTierRow[];
	affiliate_id: string;
	now: number;
}

export interface CreateBonusTierBody {
	bonus_id: string;
	campaign_id: string;
	tier_name: string;
	threshold_conversions: number;
	bonus_amount_cents: number;
	starts_at: number;
	ends_at: number;
	display_order?: number;
}

export interface CreateBonusTierResponse {
	ok: boolean;
	bonus_id: string;
	created_at: number;
}

const BASE = "/api/v1/bonus-structure-display-for-partners";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListBonusStructureResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListBonusStructureResponse>(res);
}

export async function listForPartner(params: {
	affiliate_id: string;
	campaign_id?: string;
}): Promise<PartnerBonusResponse> {
	const q = new URLSearchParams();
	q.set("affiliate_id", params.affiliate_id);
	if (params.campaign_id) q.set("campaign_id", params.campaign_id);
	const res = await fetch(`${BASE}/for-partner?${q.toString()}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<PartnerBonusResponse>(res);
}

export async function create(body: CreateBonusTierBody): Promise<CreateBonusTierResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateBonusTierResponse>(res);
}

export const bonusStructureDisplayForPartnersQueryKeys = {
	all: ["bonus-structure-display-for-partners"] as const,
	list: () => [...bonusStructureDisplayForPartnersQueryKeys.all, "list"] as const,
	partner: (affiliate_id: string, campaign_id?: string) =>
		[
			...bonusStructureDisplayForPartnersQueryKeys.all,
			"partner",
			affiliate_id,
			campaign_id ?? "",
		] as const,
};

/**
 * Pure: pick the highest-tier row a partner has reached given conversion count.
 * Rows must share the same campaign. Returns null if none reached.
 */
export function pickTier(
	rows: BonusTierRow[],
	conversions: number,
): BonusTierRow | null {
	if (!Array.isArray(rows) || rows.length === 0) return null;
	let best: BonusTierRow | null = null;
	for (const r of rows) {
		if (conversions < r.threshold_conversions) continue;
		if (!best || r.threshold_conversions > best.threshold_conversions) {
			best = r;
		}
	}
	return best;
}

/**
 * Pure: compute the bonus earned given conversion count + tiers.
 * Returns cents.
 */
export function calcBonusCents(
	rows: BonusTierRow[],
	conversions: number,
): number {
	const t = pickTier(rows, conversions);
	return t ? t.bonus_amount_cents : 0;
}

/**
 * Pure: format cents → "$X.YZ" (USD-style, no localization).
 */
export function formatCents(cents: number): string {
	if (!Number.isFinite(cents)) return "$0.00";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(Math.round(cents));
	const dollars = Math.floor(abs / 100);
	const rem = abs % 100;
	return `${sign}$${dollars.toLocaleString("en-US")}.${rem.toString().padStart(2, "0")}`;
}

/**
 * Pure: is the row currently within its active window at `now` (unix-seconds)?
 */
export function isActive(row: BonusTierRow, now: number): boolean {
	return row.starts_at <= now && now <= row.ends_at;
}
